/* eslint no-param-reassign: "error" */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  requestStatusType,
  FileType,
  FolderType,
  Pagination,
  Creator,
  FolderPermission,
  SortBy,
} from '../../types';
import {
  FolderActionsResponse,
  IVFSResponse,
  createNewFolder,
  fetchFileSystem,
  folderActionsV2,
  getDownloadUrl,
  getFolders,
  updateFolderPermissions,
  UpdateFolderPermissionsResponse,
} from '../../services/fileSystemService';
import { RootState } from '../../reducers';
import { deleteFile, moveFile } from '../selectedVideo/selectedVideoSlice';
import { downloadMultipleVideos } from '../../lib/videoUtils';
import { ProfileType } from '../../constants/mediaFormatProfiles';
import { DEMO_VIDEO } from '../../constants/demoVideo';
import { sortFilesLocally } from '../../lib/sortFiles';

export interface LibraryState {
  status: requestStatusType;
  selectedFolder: FolderType | null;
  folders: {
    status: requestStatusType;
    data: FolderType[];
  };
  files: {
    status: requestStatusType;
    data: FileType[]; // Files of selected folder
    pagination: Pagination;
    librarySize: number;
    freeUserTimeLimit: Date | null;
    isFromCache: boolean; // True if data was loaded from cache
  };
  selectedFiles: {
    [key: string]: FileType;
  };
  actions: {
    add: {
      status: requestStatusType;
    };
    rename: {
      status: requestStatusType;
    };
    manage: {
      status: requestStatusType;
    };
    delete: {
      status: requestStatusType;
    };
    permissions: {
      status: requestStatusType;
    };
  };
  creators: Creator[];
  lastRefreshTriggered: number; // Timestamp to trigger refresh
  lastRefreshCompleted: number; // Timestamp to complete refresh
  deletingFileIds: string[]; // Track files currently being deleted
  movingFileIds: string[]; // Track files currently being moved
}

const initialState: LibraryState = {
  status: 'idle',
  selectedFolder: null,
  folders: {
    status: 'idle',
    data: [],
  },
  files: {
    status: 'idle',
    data: [],
    librarySize: 0,
    freeUserTimeLimit: null,
    pagination: {
      total: 0,
      page: 0,
      limit: 10,
    },
    isFromCache: false,
  },
  selectedFiles: {},
  actions: {
    add: {
      status: 'idle',
    },
    rename: {
      status: 'idle',
    },
    manage: {
      status: 'idle',
    },
    delete: {
      status: 'idle',
    },
    permissions: {
      status: 'idle',
    },
  },
  creators: [],
  lastRefreshTriggered: 0,
  lastRefreshCompleted: 0,
  deletingFileIds: [],
  movingFileIds: [],
};

export type FolderActionsType = 'add' | 'rename' | 'manage' | 'delete';

export interface FolderActionsParams {
  folderId: string;
  action: FolderActionsType;
  data?: {
    newName?: string;
  };
}

export const folderActions = createAsyncThunk<
  IVFSResponse<FolderActionsResponse>,
  FolderActionsParams
>('library/folderActions', async ({ folderId, action, data }, thunkAPI) => {
  switch (action) {
    case 'rename':
      if (!data?.newName) {
        throw new Error('New name is required for rename action');
      }
      break;
    default:
  }
  try {
    const response = await folderActionsV2({
      folderId,
      action,
      data,
    });
    if (response.data.success) {
      return response.data;
    }
    throw new Error('server error');
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const createFolder = createAsyncThunk<
  FolderType,
  string,
  { state: RootState }
>('library/createFolder', async (name, { getState, rejectWithValue }) => {
  try {
    const { team } = getState();
    const teamId = team.selectedTeam?.teamId;
    if (!teamId) {
      throw new Error('Team ID is not available.');
    }
    const response = await createNewFolder({
      name,
      teamId,
    });
    return response.data.data.folder;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

type FetchFilesReturnType = {
  files: FileType[];
  pagination: Pagination;
  librarySize: number;
  freeUserTimeLimit: Date | null;
};
export const fetchFiles = createAsyncThunk<
  FetchFilesReturnType,
  Parameters<typeof fetchFileSystem>[0],
  { state: RootState }
>('library/fetchFiles', async (params, thunkAPI) => {
  const data = await fetchFileSystem(params);
  const { fileSystem, pagination, librarySize, freeUserTimeLimit } =
    data.data.data;

  // Create an action to complete the refresh
  thunkAPI.dispatch({ type: 'library/completeFileListRefresh' });

  return {
    files: fileSystem,
    librarySize,
    pagination,
    freeUserTimeLimit,
  };
});

interface FetchFoldersParams {
  teamId: string;
}
interface FetchFoldersReturnType {
  folders: FolderType[];
}

export const fetchFolders = createAsyncThunk<
  FetchFoldersReturnType,
  FetchFoldersParams
>('library/fetchFolders', async ({ teamId }) => {
  const response = await getFolders({ teamId });
  const { folders } = response.data.data;
  return { folders };
});

export const downloadMultipleFiles = createAsyncThunk(
  'library/downloadMultipleFiles',
  async (files: FileType[], { rejectWithValue }) => {
    try {
      const downloadData = await Promise.all(
        files.map(async (fileData) => {
          try {
            const { url } = await getDownloadUrl({
              fileId: fileData._id,
              profile: fileData.defaultProfile as ProfileType,
            });
            if (url) {
              return {
                success: true,
                url,
                fileId: fileData._id,
                name: fileData.name || fileData._id,
              };
            }
            return {
              success: false,
              fileId: fileData._id,
              error: 'No download URL available',
            };
          } catch (error) {
            return {
              success: false,
              fileId: fileData._id,
              error: error.message,
            };
          }
        }),
      );

      const failedDownloads = downloadData.filter((result) => !result.success);
      const successfulDownloads = downloadData.filter(
        (result) => result.success,
      );

      if (failedDownloads.length > 0) {
        // Still proceed with successful downloads
        if (successfulDownloads.length > 0) {
          downloadMultipleVideos(
            successfulDownloads.map((download) => ({
              url: download.url!,
              name: download.name!,
            })),
          );
        }

        return rejectWithValue({
          message: `${failedDownloads.length} of ${files.length} files failed to download`,
          failedDownloads,
          successfulDownloads: successfulDownloads.length,
        });
      }

      // Trigger all downloads with staggered timing
      downloadMultipleVideos(
        successfulDownloads.map((download) => ({
          url: download.url!,
          name: download.name!,
        })),
      );

      return downloadData;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  },
);

export const addFileTag = createAsyncThunk(
  'library/addFileTag',
  async ({
    fileId,
    tag,
  }: {
    fileId: string;
    tag: { key: string; value: string };
  }) => {
    await addFileTag({ fileId, tag });
  },
);

export const updatePermissions = createAsyncThunk(
  'library/updatePermissions',
  async (
    {
      folderId,
      permissions,
    }: {
      folderId: string;
      permissions: FolderPermission[] | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateFolderPermissions(folderId, permissions);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    selectFolder: (state, action: PayloadAction<FolderType>) => {
      state.selectedFolder = action.payload;
    },
    setFolders: (state, action: PayloadAction<FolderType[]>) => {
      state.folders.data = action.payload;
    },
    selectFile: (state, action: PayloadAction<FileType>) => {
      const file = action.payload;
      state.selectedFiles = {
        [file._id]: file,
      };
    },
    selectSingleFile: (
      state,
      action: PayloadAction<{
        fileId: string;
      }>,
    ) => {
      state.selectedFiles = {};
      const file = state.files.data.find(
        (f) => f._id === action.payload.fileId,
      );
      if (file) {
        state.selectedFiles[file._id] = file;
      }
    },
    deselectFile: (state, action: PayloadAction<FileType>) => {
      const file = action.payload;
      delete state.selectedFiles[file._id];
    },
    deselectAllFiles: (state) => {
      state.selectedFiles = {};
    },
    toggleSelectFile: (state, action: PayloadAction<FileType>) => {
      const file = action.payload;
      if (state.selectedFiles[file._id]) {
        delete state.selectedFiles[file._id];
      } else {
        state.selectedFiles[file._id] = file;
      }
    },
    addFile: (state, action: PayloadAction<FileType>) => {
      state.files.data.unshift(action.payload);
    },
    updateFile: (
      state,
      action: PayloadAction<Pick<FileType, '_id' | 'name'>>,
    ) => {
      const { _id, name } = action.payload;
      state.files.data = state.files.data.map((f) =>
        f._id === _id ? { ...f, name } : f,
      );
    },
    triggerFileListRefresh: (state) => {
      state.lastRefreshTriggered = Date.now();
    },
    completeFileListRefresh: (state) => {
      state.lastRefreshCompleted = Date.now();
    },
    optimisticSort: (
      state,
      action: PayloadAction<{ sortBy: SortBy; sortOrder: 'asc' | 'desc' }>,
    ) => {
      // Sort files in memory immediately for instant UI feedback
      const { sortBy, sortOrder } = action.payload;
      state.files.data = sortFilesLocally(state.files.data, sortBy, sortOrder);
      // Set status to pending to show loading indicator
      state.files.status = 'pending';
    },
    updateFolder: (state, action: PayloadAction<FolderType>) => {
      const updatedFolder = action.payload;
      // Update in folders list
      const index = state.folders.data.findIndex(
        (f) => f._id === updatedFolder._id,
      );
      if (index !== -1) {
        state.folders.data[index] = updatedFolder;
      }
      // Update selectedFolder if it matches
      if (state.selectedFolder?._id === updatedFolder._id) {
        state.selectedFolder = updatedFolder;
      }
    },
    clearFiles: (state) => {
      // Clear files when navigating to a new folder/team to prevent stale data
      state.files.data = [];
      state.files.status = 'idle';
      state.files.pagination = { total: 0, page: 0, limit: 20 };
      state.files.isFromCache = false;
      state.selectedFiles = {};
    },
    hydrateFilesFromCache: (
      state,
      action: PayloadAction<{
        files: FileType[];
        pagination: Pagination;
        librarySize: number;
      }>,
    ) => {
      // Only hydrate if we don't already have fresh data
      if (state.files.data.length === 0 && state.files.status !== 'succeeded') {
        state.files.data = action.payload.files;
        state.files.pagination = action.payload.pagination;
        state.files.librarySize = action.payload.librarySize;
        state.files.isFromCache = true;
        // Keep status as 'idle' so skeletons don't show, but the view knows data is available
        state.files.status = 'succeeded';
      }
    },
    hydrateFoldersFromCache: (state, action: PayloadAction<FolderType[]>) => {
      // Only hydrate if we don't already have data
      if (
        state.folders.data.length === 0 &&
        state.folders.status !== 'succeeded'
      ) {
        state.folders.data = action.payload;
        state.folders.status = 'succeeded';
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createFolder.pending, (state) => {
      state.folders.status = 'pending';
    });
    builder.addCase(
      createFolder.fulfilled,
      (state, action: PayloadAction<FolderType>) => {
        state.folders.status = 'idle';
        state.folders.data.push(action.payload);
      },
    );
    builder.addCase(createFolder.rejected, (state) => {
      state.folders.status = 'failed';
    });
    // Files
    builder.addCase(fetchFiles.pending, (state) => {
      state.files.status = 'pending';
    });
    builder.addCase(fetchFiles.fulfilled, (state, action) => {
      state.files.status = 'succeeded';
      state.files.isFromCache = false; // Fresh data from server

      const fetchedFiles = action.payload.files;
      const isFirstPage = !action.meta.arg?.page || action.meta.arg.page === 1;

      // Filter out any files that are currently being deleted
      const filteredFiles = fetchedFiles.filter(
        (file) => !state.deletingFileIds.includes(file._id),
      );

      if (action.meta.arg?.page && action.meta.arg.page > 1) {
        state.files.data = [...state.files.data, ...filteredFiles];
      } else {
        // Check if we should show demo video for empty library on first page
        const shouldInjectDemo =
          isFirstPage &&
          filteredFiles.length === 0 &&
          action.meta.arg?.folderId === 'recents';

        if (shouldInjectDemo) {
          // Inject demo video with proper teamId and spaceId
          const demoVideo = {
            ...DEMO_VIDEO,
            teamId: action.meta.arg?.teamId || '',
            spaceId: action.meta.arg?.teamId || '', // Use teamId as spaceId for now
          };
          state.files.data = [demoVideo];
        } else {
          state.files.data = filteredFiles;
        }
        state.selectedFiles = {};
      }
      state.files.pagination = action.payload.pagination;
      state.files.freeUserTimeLimit = action.payload.freeUserTimeLimit;
    });
    builder.addCase(fetchFiles.rejected, (state) => {
      state.files.status = 'failed';
    });
    // Folders
    builder.addCase(fetchFolders.pending, (state) => {
      state.folders.status = 'pending';
    });
    builder.addCase(fetchFolders.fulfilled, (state, action) => {
      state.folders.status = 'succeeded';
      state.folders.data = action.payload.folders;
    });
    builder.addCase(fetchFolders.rejected, (state) => {
      state.folders.status = 'failed';
    });
    // Folder Actions
    builder.addCase(folderActions.pending, (state, action) => {
      const folderActionType = action.meta.arg.action;
      state.actions[folderActionType].status = 'pending';
    });
    builder.addCase(
      folderActions.fulfilled,
      (state, action: PayloadAction<IVFSResponse<FolderActionsResponse>>) => {
        const folderActionType = action.payload.data.folderAction;
        state.actions[folderActionType].status = 'succeeded';
        state.folders.data = action.payload.data.folders;
      },
    );
    builder.addCase(folderActions.rejected, (state, action) => {
      const folderActionType = action.meta.arg.action;
      state.actions[folderActionType].status = 'failed';
    });
    builder.addCase(deleteFile.pending, (state, action) => {
      const fileId = action.meta.arg;
      // Track that this file is being deleted
      state.deletingFileIds.push(fileId);
      // Optimistically remove from UI
      state.files.data = state.files.data.filter((file) => file._id !== fileId);
    });
    builder.addCase(deleteFile.fulfilled, (state, action) => {
      const fileId = action.meta.arg;
      // Remove from deleting tracking
      state.deletingFileIds = state.deletingFileIds.filter(
        (id) => id !== fileId,
      );
      // Ensure file stays removed after successful deletion
      state.files.data = state.files.data.filter((file) => file._id !== fileId);
    });
    builder.addCase(deleteFile.rejected, (state, action) => {
      const fileId = action.meta.arg;
      // Remove from deleting tracking
      state.deletingFileIds = state.deletingFileIds.filter(
        (id) => id !== fileId,
      );
      // On deletion failure, trigger a refresh to restore the file
      state.lastRefreshTriggered = Date.now();
    });
    // Move file handlers
    builder.addCase(moveFile.pending, (state, action) => {
      const { fileId } = action.meta.arg;
      // Track that this file is being moved
      if (!state.movingFileIds.includes(fileId)) {
        state.movingFileIds.push(fileId);
      }
    });
    builder.addCase(moveFile.fulfilled, (state, action) => {
      const { fileId, toFolderId } = action.meta.arg;
      // Remove from moving tracking
      state.movingFileIds = state.movingFileIds.filter((id) => id !== fileId);

      // Check URL to determine current view
      const urlHash = window.location.hash;
      const isRecentsView =
        urlHash.endsWith('/recents') || urlHash.includes('/recents?');

      // Extract current folder ID from URL (pattern: /library/{teamId}/{folderId})
      const folderIdMatch = urlHash.match(/\/library\/[^/]+\/([^/?]+)/);
      const currentFolderId = folderIdMatch ? folderIdMatch[1] : null;

      // Get the file to check its current parentId
      const file = state.files.data.find((f) => f._id === fileId);
      const fileCurrentParentId = file?.parentId;

      if (isRecentsView) {
        // In recents view, update the file's parentId to reflect the new folder
        // Keep the file visible with updated parentId
        state.files.data = state.files.data.map((f) =>
          f._id === fileId ? { ...f, parentId: toFolderId } : f,
        );
      } else if (currentFolderId && currentFolderId !== 'recents') {
        // In a specific folder view
        // Check if the file is being moved OUT of the current folder OR INTO the current folder
        if (
          fileCurrentParentId === currentFolderId &&
          toFolderId !== currentFolderId
        ) {
          // File is being moved OUT of the current folder - remove it
          state.files.data = state.files.data.filter((f) => f._id !== fileId);
        } else if (
          toFolderId === currentFolderId &&
          fileCurrentParentId !== currentFolderId
        ) {
          // File is being moved INTO the current folder - it will appear on next refresh
          // Trigger refresh to fetch the newly moved file
          state.lastRefreshTriggered = Date.now();
        }
        // If moving within the same folder (shouldn't happen, but just in case), do nothing
      } else {
        // Fallback: remove file from view
        state.files.data = state.files.data.filter((f) => f._id !== fileId);
      }
    });
    builder.addCase(moveFile.rejected, (state, action) => {
      const { fileId } = action.meta.arg;
      // Remove from moving tracking
      state.movingFileIds = state.movingFileIds.filter((id) => id !== fileId);
    });
    builder.addCase(updatePermissions.pending, (state) => {
      state.actions.permissions.status = 'pending';
    });
    builder.addCase(
      updatePermissions.fulfilled,
      (state, action: PayloadAction<UpdateFolderPermissionsResponse>) => {
        state.actions.permissions.status = 'succeeded';
        const index = state.folders.data.findIndex(
          (folder) => folder._id === action.payload.folder._id,
        );
        if (index !== -1) {
          state.folders.data[index] = action.payload.folder;
        }
        if (state.selectedFolder?._id === action.payload.folder._id) {
          state.selectedFolder = action.payload.folder;
        }
      },
    );
    builder.addCase(updatePermissions.rejected, (state) => {
      state.actions.permissions.status = 'failed';
    });

    // Download handling
    builder.addCase(downloadMultipleFiles.fulfilled, (state) => {
      // Clear selected files after successful download
      state.selectedFiles = {};
    });
    builder.addCase(downloadMultipleFiles.rejected, (state, action) => {
      // Keep selected files on failure so user can retry
      console.error('Download failed:', action.payload);
    });
  },
});

export const {
  selectFolder,
  setFolders,
  toggleSelectFile,
  deselectAllFiles,
  addFile,
  selectSingleFile,
  updateFile,
  triggerFileListRefresh,
  completeFileListRefresh,
  optimisticSort,
  updateFolder,
  clearFiles,
  hydrateFilesFromCache,
  hydrateFoldersFromCache,
} = librarySlice.actions;
export default librarySlice.reducer;

export const selectTotalFiles = (state: RootState) => {
  return state.library.files.data.length;
};
